/**
 * XML prologue safety analysis (§30).
 *
 * OOXML, OWPML, ODF, SVG, EPUB and plain `.xml` all reach a parser, and all of
 * them can carry a DTD. Two classic attacks follow:
 *
 * - **XXE** — an external entity pointing at `file:///…` or an internal URL,
 *   turning a document viewer into a file-disclosure or SSRF primitive.
 * - **Entity expansion (billion laughs)** — nested entities that expand to
 *   gigabytes from a few hundred bytes, exhausting memory before any limit on
 *   *file* size can help.
 *
 * The defence is to refuse the DTD, not to parse it carefully. This module
 * decides that before a parser is constructed; the estimate exists so the user
 * can be told *why* a file was refused rather than just that it failed.
 */

export interface EntityDeclaration {
  readonly name: string;
  /** Literal replacement text, when the entity is internal. */
  readonly value: string | undefined;
  /** SYSTEM/PUBLIC identifier, when the entity is external. */
  readonly externalId: string | undefined;
}

export type XmlVerdict = 'safe' | 'strip-dtd' | 'reject';

export interface XmlSafetyReport {
  readonly hasDoctype: boolean;
  readonly hasInternalEntities: boolean;
  readonly hasExternalEntity: boolean;
  readonly externalReferences: readonly string[];
  readonly entities: readonly EntityDeclaration[];
  /**
   * Upper bound on expanded size in characters, capped at the analysis budget.
   * `Infinity` means the expansion is recursive or exceeded the budget.
   */
  readonly estimatedExpansion: number;
  readonly verdict: XmlVerdict;
  readonly reasons: readonly string[];
}

/** How far the analyser itself will count before declaring the file hostile. */
const EXPANSION_ANALYSIS_CAP = 10_000_000;

/** DTDs never appear far into a document; scanning the head is sufficient. */
const PROLOGUE_SCAN_BYTES = 64 * 1024;

const DOCTYPE_RE = /<!DOCTYPE\s+[^>[]*(\[[\s\S]*?\])?\s*>/i;
const ENTITY_RE = /<!ENTITY\s+(%\s+)?([A-Za-z_][\w.-]*)\s+(?:(SYSTEM|PUBLIC)\s+(?:"([^"]*)"|'([^']*)')(?:\s+(?:"([^"]*)"|'([^']*)'))?|"([^"]*)"|'([^']*)')/gi;
const EXTERNAL_IN_DOCTYPE_RE = /<!DOCTYPE\s+\S+\s+(SYSTEM|PUBLIC)\s+(?:"([^"]*)"|'([^']*)')/i;

/**
 * Expanded length of a value, following entity references.
 *
 * Memoised and capped so that analysing a billion-laughs file costs
 * milliseconds — the analyser must not become the denial of service it is
 * meant to prevent. Cycles resolve to `Infinity` rather than recursing.
 */
function expandedLength(
  value: string,
  entities: ReadonlyMap<string, string>,
  memo: Map<string, number>,
  inProgress: Set<string>,
): number {
  let total = 0;
  const refRe = /&([A-Za-z_][\w.-]*);/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = refRe.exec(value)) !== null) {
    total += match.index - lastIndex;
    lastIndex = refRe.lastIndex;

    const name = match[1]!;
    const target = entities.get(name);
    if (target === undefined) {
      total += match[0].length; // unresolved reference stays literal
    } else if (inProgress.has(name)) {
      return Number.POSITIVE_INFINITY; // recursive definition
    } else {
      let sub = memo.get(name);
      if (sub === undefined) {
        inProgress.add(name);
        sub = expandedLength(target, entities, memo, inProgress);
        inProgress.delete(name);
        memo.set(name, sub);
      }
      if (!Number.isFinite(sub)) return Number.POSITIVE_INFINITY;
      total += sub;
    }

    if (total > EXPANSION_ANALYSIS_CAP) return Number.POSITIVE_INFINITY;
  }

  total += value.length - lastIndex;
  return total;
}

/**
 * Analyse the XML prologue.
 *
 * @param text Document text, or its first {@link PROLOGUE_SCAN_BYTES} characters.
 * @param maxEntityExpansions Budget from the active {@link ResourceLimits}.
 */
export function analyzeXmlSafety(text: string, maxEntityExpansions = 10_000): XmlSafetyReport {
  const head = text.slice(0, PROLOGUE_SCAN_BYTES);
  const reasons: string[] = [];
  const externalReferences: string[] = [];

  const doctypeMatch = DOCTYPE_RE.exec(head);
  if (!doctypeMatch) {
    return {
      hasDoctype: false, hasInternalEntities: false, hasExternalEntity: false,
      externalReferences: [], entities: [], estimatedExpansion: 0,
      verdict: 'safe', reasons: [],
    };
  }

  const doctype = doctypeMatch[0];
  reasons.push('document declares a DTD');

  const externalDoctype = EXTERNAL_IN_DOCTYPE_RE.exec(doctype);
  if (externalDoctype) {
    const id = externalDoctype[2] ?? externalDoctype[3];
    if (id) externalReferences.push(id);
  }

  const entities: EntityDeclaration[] = [];
  const internalValues = new Map<string, string>();
  ENTITY_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = ENTITY_RE.exec(doctype)) !== null) {
    const name = m[2]!;
    const externalId = m[4] ?? m[5];
    const value = m[8] ?? m[9];
    if (externalId !== undefined) {
      externalReferences.push(externalId);
      entities.push({ name, value: undefined, externalId });
    } else {
      internalValues.set(name, value ?? '');
      entities.push({ name, value: value ?? '', externalId: undefined });
    }
  }

  const hasExternalEntity = externalReferences.length > 0;
  if (hasExternalEntity) {
    reasons.push(`external reference(s): ${externalReferences.slice(0, 3).join(', ')}`);
  }

  // Worst-case expansion across every declared entity plus the document body.
  const memo = new Map<string, number>();
  let estimatedExpansion = 0;
  for (const [name, value] of internalValues) {
    const len = expandedLength(value, internalValues, memo, new Set([name]));
    if (!Number.isFinite(len)) { estimatedExpansion = Number.POSITIVE_INFINITY; break; }
    estimatedExpansion = Math.max(estimatedExpansion, len);
  }

  if (!Number.isFinite(estimatedExpansion)) {
    reasons.push('entity expansion is recursive or exceeds the analysis budget');
  } else if (estimatedExpansion > maxEntityExpansions) {
    reasons.push(`entity expansion reaches ${estimatedExpansion} characters (limit ${maxEntityExpansions})`);
  }

  const expansionHostile = !Number.isFinite(estimatedExpansion)
    || estimatedExpansion > maxEntityExpansions;

  // An external reference or a bomb is a refusal. A merely-present DTD with
  // benign internal entities is stripped: plenty of legitimate XML declares
  // one, and refusing those would fail users for no security gain.
  const verdict: XmlVerdict = hasExternalEntity || expansionHostile ? 'reject' : 'strip-dtd';

  return {
    hasDoctype: true,
    hasInternalEntities: internalValues.size > 0,
    hasExternalEntity,
    externalReferences,
    entities,
    estimatedExpansion,
    verdict,
    reasons,
  };
}

/**
 * Remove the DTD so a parser cannot act on it.
 *
 * Used for `verdict === 'strip-dtd'`. Refused documents are never handed to a
 * parser at all, so this is not a sanitiser of last resort — it is the normal
 * path for ordinary XML that happens to declare a doctype.
 */
export function stripDoctype(text: string): string {
  return text.replace(DOCTYPE_RE, '');
}

/**
 * Parser options that every XML parse in the product must use.
 *
 * Exported as data so the setting is reviewable in one place rather than
 * repeated at each `new XMLParser(...)` call site.
 */
export const SAFE_XML_PARSER_OPTIONS = Object.freeze({
  processEntities: false,
  allowBooleanAttributes: true,
  ignoreDeclaration: false,
  ignorePiTags: true,
  htmlEntities: false,
});
